import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  JOGKÖR-HOZZÁRENDELÉS.
 *
 *  A jogkör eddig mellékesen, szétszórtan volt szerkeszthető: a dolgozónál az
 *  állomány-adatlap egyik mezőjeként (EGY jogkör, alrendszer nélkül), a szülőnél
 *  sehogy. Aki azt kérdezte, „ki az iskolatitkár", vagy „adjunk könyvelő jogot a
 *  kolléganőnek", annak személyenként kellett adatlapot nyitnia — és nem is látta,
 *  kinek mi van.
 *
 *  Itt egy helyen van: kereshető névsor a jelenlegi jogkörökkel, alrendszerenkénti
 *  szerkesztő, és tömeges hozzáadás/elvétel. A tényleges írást a kiszolgáló végzi és
 *  naplózza — a felület csak összeállítja, mi legyen a végállapot.
 */
@Component({
  selector: 'app-admin-jogkorok',
  templateUrl: './jogkorok.component.html',
  styleUrls: ['./jogkorok.component.scss'],
})
export class AdminJogkorokComponent implements OnInit {
  intezmeny = '';
  toltodik = true;
  hiba = '';
  ment = false;

  fajta: 'dolgozo' | 'gondviselo' = 'dolgozo';
  /*  Gondviselő CSAK az iskolában (SULINFORM) van. A többi modulban a felhasználó
   *  munkatárs vagy ügyfél – a kiszolgáló ott csak a dolgozói kört adja (`mode`). */
  mode = 'SULINFORM';
  get sulinform(): boolean { return this.mode === 'SULINFORM'; }
  fajtak: any[] = [];
  alrendszerek: any[] = [];      // [{ alrendszer, jogkorok: [{jogkor,name,admin,orokol}] }]
  tetelek: any[] = [];

  szuro = '';
  csakKilepettNelkul = true;

  /** Kijelölés a tömeges művelethez: azonosító -> igaz. */
  kijelolt = new Set<string>();

  /** A nyitott szerkesztő (egy emberé). */
  szerkesztett: any = null;
  /** A szerkesztőben épp beállított állapot: alrendszer -> Set(jogkör). */
  szerkesztettJogkorok = new Map<string, Set<string>>();

  // Tömeges művelet
  tomegesAlrendszer = 'SULINFORM';
  tomegesJogkor = '';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    this.mode = String((await this.data.getFromStorage('currentMode')) || 'SULINFORM');
    await this.betolt();
  }

  get intezmenyPath(): string { return encodeURI(this.intezmeny); }

  async betolt() {
    this.toltodik = true;
    this.hiba = '';
    this.kijelolt.clear();
    try {
      const [kat, lista]: any[] = await Promise.all([
        firstValueFrom(this.http.get(`/api/admin/jogkorok/katalogus/${this.intezmenyPath}?mode=${encodeURIComponent(this.mode)}`)),
        firstValueFrom(this.http.get(
          `/api/admin/jogkorok/lista/${this.intezmenyPath}?fajta=${this.fajta}&mode=${encodeURIComponent(this.mode)}`)),
      ]);
      this.fajtak = kat?.fajtak || [];
      this.alrendszerek = kat?.alrendszerek || [];
      this.tetelek = lista?.tetelek || [];
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A lista betöltése nem sikerült.';
      this.tetelek = [];
    } finally {
      this.toltodik = false;
    }
  }

  async fajtaValt(uj: any) {
    this.fajta = uj === 'gondviselo' ? 'gondviselo' : 'dolgozo';
    await this.betolt();
  }

  /* ── Névsor ─────────────────────────────────────────────────────────────── */

  get szurtTetelek(): any[] {
    const k = this.szuro.trim().toLowerCase();
    return (this.tetelek || []).filter((t) => {
      if (this.csakKilepettNelkul && t.kilepett) return false;
      if (!k) return true;
      const jogkorSzoveg = this.jogkorLista(t).map((j: any) => `${j.jogkor} ${j.nev}`).join(' ');
      return `${t.nev} ${t.azonosito} ${t.email || ''} ${t.megjegyzes || ''} ${jogkorSzoveg}`
        .toLowerCase().includes(k);
    });
  }

  /** Egy ember jogkörei kilapítva, megjelenítéshez. */
  jogkorLista(tetel: any): any[] {
    const ki: any[] = [];
    for (const [alr, lista] of Object.entries(tetel?.jogkorok || {})) {
      for (const j of (lista as string[]) || []) {
        ki.push({ alrendszer: alr, jogkor: j, nev: this.jogkorNev(alr, j), admin: this.adminJogkor(alr, j) });
      }
    }
    return ki.sort((a, b) => a.alrendszer.localeCompare(b.alrendszer)
      || a.nev.localeCompare(b.nev, 'hu'));
  }

  private jogkorMeta(alrendszer: string, jogkor: string): any {
    const cs = (this.alrendszerek || []).find((x) => x.alrendszer === alrendszer);
    return (cs?.jogkorok || []).find((j: any) => j.jogkor === jogkor) || null;
  }
  jogkorNev(alrendszer: string, jogkor: string): string {
    return this.jogkorMeta(alrendszer, jogkor)?.name || jogkor;
  }
  adminJogkor(alrendszer: string, jogkor: string): boolean {
    return !!this.jogkorMeta(alrendszer, jogkor)?.admin;
  }
  /** Ismeretlen jogkör: a törzsben nincs ilyen kód — jelezzük, ne tűnjön el csendben. */
  ismeretlen(alrendszer: string, jogkor: string): boolean {
    return !this.jogkorMeta(alrendszer, jogkor);
  }

  /* ── Kijelölés ──────────────────────────────────────────────────────────── */

  kijeloltE(t: any): boolean { return this.kijelolt.has(this.kulcs(t)); }
  kijelolValt(t: any) {
    const k = this.kulcs(t);
    if (this.kijelolt.has(k)) this.kijelolt.delete(k); else this.kijelolt.add(k);
  }
  mindKijelol() {
    const lista = this.szurtTetelek;
    const mind = lista.every((t) => this.kijelolt.has(this.kulcs(t)));
    for (const t of lista) {
      if (mind) this.kijelolt.delete(this.kulcs(t)); else this.kijelolt.add(this.kulcs(t));
    }
  }
  /*  A dolgozónál a kulcs az (azonosító, feladatellátás) páros: ugyanaz az ember más
   *  intézményben más szerepben dolgozhat, és a jogkör a feladatellátáshoz tartozik. */
  private kulcs(t: any): string { return `${t.azonosito}|${t.feladatellatas || ''}`; }

  /* ── Egyéni szerkesztés ─────────────────────────────────────────────────── */

  szerkesztoNyit(t: any) {
    this.szerkesztett = t;
    this.szerkesztettJogkorok = new Map();
    for (const [alr, lista] of Object.entries(t?.jogkorok || {})) {
      this.szerkesztettJogkorok.set(alr, new Set((lista as string[]) || []));
    }
  }
  szerkesztoZar() { this.szerkesztett = null; }

  vanBejelolve(alrendszer: string, jogkor: string): boolean {
    return !!this.szerkesztettJogkorok.get(alrendszer)?.has(jogkor);
  }
  bejelolValt(alrendszer: string, jogkor: string) {
    if (!this.szerkesztettJogkorok.has(alrendszer)) this.szerkesztettJogkorok.set(alrendszer, new Set());
    const s = this.szerkesztettJogkorok.get(alrendszer)!;
    if (s.has(jogkor)) s.delete(jogkor); else s.add(jogkor);
  }

  /*  A TÖRZSBEN NEM SZEREPLŐ JOGKÖRÖK.
   *
   *  Régi vagy elgépelt kódok, illetve olyan alrendszeré, aminek nincs jogkör-törzse
   *  (pl. FITNESS). A mentés MEGTARTJA őket – csendben eldobni a legrosszabb válasz
   *  lenne –, de a szerkesztőben nem lenne hozzájuk jelölőnégyzet, tehát elvenni sem
   *  lehetne őket. Ezért itt külön megjelennek, és egyenként kivehetők. */
  get egyebJogkorok(): any[] {
    const ki: any[] = [];
    for (const [alr, s] of this.szerkesztettJogkorok.entries()) {
      for (const j of s) {
        if (this.ismeretlen(alr, j)) ki.push({ alrendszer: alr, jogkor: j });
      }
    }
    return ki;
  }

  egyebElvesz(alrendszer: string, jogkor: string) {
    this.szerkesztettJogkorok.get(alrendszer)?.delete(jogkor);
  }

  async szerkesztoMent() {
    if (!this.szerkesztett) return;
    const jogkorok: any = {};
    for (const [alr, s] of this.szerkesztettJogkorok.entries()) {
      if (s.size) jogkorok[alr] = [...s];
    }
    await this.kuld([{
      azonosito: this.szerkesztett.azonosito,
      feladatellatas: this.szerkesztett.feladatellatas,
      jogkorok,
    }]);
    this.szerkesztoZar();
  }

  /* ── Tömeges művelet ────────────────────────────────────────────────────── */

  get tomegesJogkorok(): any[] {
    return (this.alrendszerek || []).find((x) => x.alrendszer === this.tomegesAlrendszer)?.jogkorok || [];
  }

  /*  A VÉGÁLLAPOTOT a felület számolja ki, és azt küldi el: így egy helyen van a
   *  szabály, hogy mi lesz az eredmény, és a kiszolgálónak nem kell külön
   *  „hozzáad/elvesz" logikát tartania. */
  async tomeges(muvelet: 'hozzaad' | 'elvesz') {
    if (!this.tomegesJogkor || !this.kijelolt.size) return;
    const nev = this.jogkorNev(this.tomegesAlrendszer, this.tomegesJogkor);
    const kerdes = muvelet === 'hozzaad'
      ? `Megkapja a(z) „${nev}" jogkört ${this.kijelolt.size} ember. Folytatod?`
      : `Elveszed a(z) „${nev}" jogkört ${this.kijelolt.size} embertől. Folytatod?`;
    if (!confirm(kerdes)) return;

    const valtozasok = this.szurtTetelek
      .filter((t) => this.kijelolt.has(this.kulcs(t)))
      .map((t) => {
        const jogkorok: any = {};
        for (const [alr, lista] of Object.entries(t.jogkorok || {})) {
          jogkorok[alr] = [...((lista as string[]) || [])];
        }
        const van = new Set(jogkorok[this.tomegesAlrendszer] || []);
        if (muvelet === 'hozzaad') van.add(this.tomegesJogkor); else van.delete(this.tomegesJogkor);
        if (van.size) jogkorok[this.tomegesAlrendszer] = [...van];
        else delete jogkorok[this.tomegesAlrendszer];
        return { azonosito: t.azonosito, feladatellatas: t.feladatellatas, jogkorok };
      });
    await this.kuld(valtozasok);
  }

  private async kuld(valtozasok: any[]) {
    if (!valtozasok.length) return;
    this.ment = true;
    try {
      const r: any = await firstValueFrom(this.http.put(
        `/api/admin/jogkorok/${this.intezmenyPath}`, { fajta: this.fajta, valtozasok, mode: this.mode }));
      this.data.sendToast(`${r?.irt ?? valtozasok.length} embernél frissült a jogkör.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.ment = false;
    }
  }
}
